import AppDatePicker from '@nxsys/components/AppDatePicker';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppSelect from '@nxsys/components/AppSelect';
import {
  genderOptions,
  irelandCountyOptions,
  titleOptions,
} from '@nxsys/constants/AppConstants';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import { Icons } from '@nxsys/types/icons';
import { Option } from '@nxsys/types/models/Common';
import { countryList, isValidNINumber } from '@nxsys/utils';
import {
  formatPostCode,
  postCodeValidationRule,
} from '@nxsys/utils/formFieldValidation';
import { Col, Form, Row } from 'antd';
import { FormProps } from 'antd/lib';
import moment, { Moment } from 'moment';
import React, { Ref, useImperativeHandle } from 'react';
import { useSelector } from 'react-redux';

export type PersonalDetailValues = {
  title: Option<string> | null;
  forename: string;
  surname: string;
  gender: Option<string> | null;
  DOB: Moment | null;
  NINumber: string;
  email: string;
  phoneNumber: string;
  address1: string;
  address2: string;
  city: string;
  town: string;
  county: string;
  postCode: string;
  country: Option<string> | null;
};

export type PersonalDetailFormRef = {
  isValidForm: () => Promise<boolean>;
  getValues: () => PersonalDetailValues;
  resetFields: () => void;
};

type PersonalDetailFormProps = {
  formRef?: Ref<PersonalDetailFormRef>;
  initialValues?: PersonalDetailValues;
};

const PersonalDetailForm: React.FC<PersonalDetailFormProps> = ({
  formRef,
  initialValues,
}) => {
  const { messages } = useTypedIntl();
  const { payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;

  const [form] = Form.useForm<PersonalDetailValues>();
  const watchDOB = Form.useWatch('DOB', form);

  const computedAge =
    watchDOB && moment(watchDOB).isValid()
      ? Math.floor(moment().diff(moment(watchDOB), 'years', true))
      : null;

  useImperativeHandle(formRef, () => ({
    isValidForm: async () => {
      try {
        await form.validateFields();
        return true;
      } catch (error) {
        return false;
      }
    },
    getValues() {
      return form.getFieldsValue(true);
    },
    resetFields() {
      form.resetFields();
    },
  }));

  const onFinish: FormProps['onFinish'] = async (values) => {
    console.info('values', values);
  };

  const onFinishFailed: FormProps['onFinishFailed'] = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  return (
    <AppFormContainer
      form={form}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      initialValues={initialValues}
      layout="vertical"
      labelCol={{ span: 24 }}
      wrapperCol={{ span: 24 }}
    >
      <div className="text-[18px] font-bold">
        {messages['common.personalDetail']}
      </div>
      <div className="text-[16px] text-gray-500 mb-[40px]">
        {messages['common.personalDetailHelperText']}
      </div>
      <Row gutter={24}>
        <Col sm={24} md={12}>
          <Form.Item
            name="title"
            label={messages['common.title']}
            rules={[{ required: true, message: messages['form.pleaseSelect'] }]}
          >
            <AppSelect
              options={titleOptions}
              placeholder={messages['form.select']}
              className="w-full !min-w-0"
            />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item
            name="forename"
            label={messages['common.forename']}
            rules={[{ required: true, message: messages['form.pleaseEnter'] }]}
          >
            <AppInput placeholder={messages['form.enter']} />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item
            name="surname"
            label={messages['common.surname']}
            rules={[{ required: true, message: messages['form.pleaseEnter'] }]}
          >
            <AppInput placeholder={messages['form.enter']} />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item
            name="gender"
            label={messages['common.gender']}
            rules={[{ required: true, message: messages['form.pleaseSelect'] }]}
          >
            <AppSelect
              options={genderOptions}
              placeholder={messages['form.select']}
              className="w-full !min-w-0"
            />
          </Form.Item>
        </Col>
        <Col sm={24} md={8}>
          <Form.Item
            name="DOB"
            label={messages['common.dateOfBirth']}
            rules={[{ required: true, message: messages['form.pleaseSelect'] }]}
          >
            <AppDatePicker
              format="DD/MM/YYYY"
              placeholder={messages['form.selectDate']}
              maxDate={moment()}
              minDate={moment().subtract(130, 'years')}
            />
          </Form.Item>
        </Col>
        <Col sm={24} md={4}>
          <Form.Item label={messages['common.age'] ?? 'Age'}>
            <AppInput
              value={computedAge !== null ? String(computedAge) : ''}
              placeholder="—"
              disabled
            />
          </Form.Item>
        </Col>
        {!isIreland && (
          <Col span={24}>
            <Form.Item
              name="NINumber"
              label={messages['common.NINumber']}
              rules={[
                {
                  validator: async (_, value) => {
                    if (!value) return;
                    if (!isValidNINumber(value)) {
                      throw new Error(
                        messages['form.invalidNINumber'] ?? 'Invalid NI number',
                      );
                    }
                  },
                },
              ]}
            >
              <AppInput
                placeholder={messages['form.enter']}
                maxLength={9}
                upperCaseAll
              />
            </Form.Item>
          </Col>
        )}
        <Col sm={24} md={12}>
          <Form.Item
            name="email"
            label={messages['common.email']}
            rules={[
              { required: true, message: messages['form.pleaseEnter'] },
              { type: 'email', message: messages['form.enterValidEmail'] },
            ]}
          >
            <AppInput
              placeholder={messages['form.enter']}
              allowCapitalize={false}
              prefix={
                <Icons.IconEmail color={defaultTheme.theme.palette.gray[500]} />
              }
            />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item name="phoneNumber" label={messages['common.phoneNumber']}>
            <AppInput
              placeholder={messages['form.enter']}
              onChange={(e) => {
                form.setFieldValue(
                  'phoneNumber',
                  e.target.value.replace(/\D/g, ''),
                );
              }}
            />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item
            name="address1"
            label={messages['common.addressLine1']}
            rules={[{ required: true, message: messages['form.pleaseEnter'] }]}
          >
            <AppInput placeholder={messages['form.enter']} />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item
            name="address2"
            label={messages['common.addressLine2']}
            rules={[{ required: true, message: messages['form.pleaseEnter'] }]}
          >
            <AppInput placeholder={messages['form.enter']} />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item name="city" label={messages['common.city']}>
            <AppInput placeholder={messages['form.enter']} maxLength={50} />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item name="county" label={messages['common.county']}>
            {isIreland ? (
              <AppSelect
                showSearch
                options={irelandCountyOptions}
                placeholder={messages['form.select']}
              />
            ) : (
              <AppInput placeholder={messages['form.enter']} />
            )}
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item
            name="postCode"
            label={
              isIreland
                ? messages['common.eirCode']
                : messages['common.postCode']
            }
            rules={[
              { required: true, message: messages['form.pleaseEnter'] },
              postCodeValidationRule(messages, payrollCountry),
            ]}
          >
            <AppInput
              placeholder={messages['form.enter']}
              upperCaseAll
              onChange={(e) => {
                form.setFieldValue(
                  'postCode',
                  formatPostCode(e.target.value, payrollCountry),
                );
              }}
              maxLength={8}
            />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item label={messages['common.country']} name="country">
            <AppSelect
              placeholder={messages['form.selectCountry']}
              options={countryList}
            />
          </Form.Item>
        </Col>
      </Row>
    </AppFormContainer>
  );
};

export default PersonalDetailForm;
